import { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import DataSetsPanel from 'components/DataSetsPanel/DataSetsPanel';
import DatePicker from 'components/DatePicker/DatePicker';
import NumberField from 'components/NumberField/NumberField';
import {
  END_DATE,
  FINISHED_GOOD_STOCK_AMOUNT, FINISHED_GOOD_STOCK_AMOUNT_CURRENCY,
  NET_BOOK, NET_BOOK_CURRENCY,
  START_DATE,
} from 'constants/formFields';
import { DATE_FORMAT } from 'constants/general';

import GrossMarginFields from './GrossMarginFields';
import RevenueFields from './RevenueFields';

import type IFinancialPanel from '../../interfaces/IFinancialPanel';

import styles from '../../sass/CreateProfitCentreView.module.scss';

function FinancialPanel({
  control,
  startDate,
  onStartDateChange,
  orgStructureData,
  trigger,
  requireNetBookWhenNoFirstLayer,
}: IFinancialPanel) {
  const { t } = useTranslation();

  const hasNoFirstLayerYet = orgStructureData === undefined || (orgStructureData?.length ?? 0) === 0;
  const isNetBookRequired = requireNetBookWhenNoFirstLayer === true && hasNoFirstLayerYet;

  useEffect(() => {
    if (trigger && requireNetBookWhenNoFirstLayer) {
      trigger(NET_BOOK).catch(() => {});
    }
  }, [trigger, requireNetBookWhenNoFirstLayer, orgStructureData]);

  const validateNetBook = (value: string | number | undefined): true | string => {
    if (!isNetBookRequired) return true;
    const isEmpty = value === undefined
      || value === null
      || value === ''
      || (typeof value === 'string' && value.trim() === '');
    if (isEmpty) {
      return t('errors.requiredField', {
        name: t('profitCentres.createProfitCentre.fields.relatedBalanceSheetAssets'),
      });
    }
    return true;
  };

  return (
    <DataSetsPanel title={t('profitCentres.createProfitCentre.financialsPanel')} className={styles.fullWidth}>
      <div className={styles.wrapper}>
        <Controller
          control={control}
          name={START_DATE}
          render={({
            field: {
              name,
              value,
              onChange,
            },
            fieldState: { error },
          }) => (
            <DatePicker
              label={t('profitCentres.createProfitCentre.fields.startDate')}
              error={error?.message}
              onChange={(date) => {
                onStartDateChange(date);
                onChange(date);
              }}
              name={name}
              value={value}
              placeholder={DATE_FORMAT}
              required
              maxDate={new Date()}
            />
          )}
        />
        <Controller
          control={control}
          name={END_DATE}
          render={({
            field: {
              name,
              value,
              onChange,
            },
            fieldState: { error },
          }) => (
            <DatePicker
              label={t('profitCentres.createProfitCentre.fields.endDate')}
              error={error?.message}
              onChange={onChange}
              name={name}
              value={value}
              minDate={startDate}
              placeholder={DATE_FORMAT}
              required
            />
          )}
        />
      </div>
      <div className={styles.wrapper}>
        <Controller
          control={control}
          name={NET_BOOK}
          rules={{ validate: validateNetBook }}
          render={({ field: { name, value, onChange }, fieldState: { error } }) => (
            <div>
              <div className={styles.fieldWithCurrency}>
                <NumberField
                  label={t('profitCentres.createProfitCentre.fields.relatedBalanceSheetAssets')}
                  name={name}
                  value={value}
                  error={error?.message}
                  onChange={(values) => onChange(values.value)}
                  placeholder={t('profitCentres.createProfitCentre.fields.relatedBalanceSheetAssets')}
                  className={styles.valueInput}
                  hideTextError
                  allowNegative={false}
                  required={isNetBookRequired}
                />
                <Controller
                  control={control}
                  name={NET_BOOK_CURRENCY}
                  render={({
                    field:
                        { value: valueCurrency },
                  }) => (
                    <span className={`${styles.currencyInput} ${error?.message ? styles.errorBorder : ''}`}>
                      {`M, ${valueCurrency}`}
                    </span>
                  )}
                />
              </div>
              {error?.message ? <span className={styles.errorMessage}>{error.message}</span> : null}
            </div>
          )}
        />
      </div>
      <RevenueFields control={control} />
      <GrossMarginFields control={control} />
      <div className={styles.wrapper}>
        <Controller
          control={control}
          name={FINISHED_GOOD_STOCK_AMOUNT}
          render={({ field: { name, value, onChange }, fieldState: { error } }) => (
            <div>
              <div className={styles.fieldWithCurrency}>
                <NumberField
                  label={t('profitCentres.createProfitCentre.fields.stock')}
                  name={name}
                  value={value}
                  error={error?.message}
                  onChange={(values) => onChange(values.value)}
                  placeholder={t('profitCentres.createProfitCentre.fields.stock')}
                  className={styles.valueInput}
                  allowNegative={false}
                  hideTextError
                  required
                />
                <Controller
                  control={control}
                  name={FINISHED_GOOD_STOCK_AMOUNT_CURRENCY}
                  render={({
                    field:
                        { value: valueCurrency },
                  }) => (
                    <span className={`${styles.currencyInput} ${error?.message ? styles.errorBorder : ''}`}>
                      {`M, ${valueCurrency}`}
                    </span>
                  )}
                />
              </div>
              {error?.message ? <span className={styles.errorMessage}>{error.message}</span> : null}
            </div>
          )}
        />
      </div>
    </DataSetsPanel>
  );
}

export default FinancialPanel;
